// /app/revision/:id/notes?at=<point or unit code>&ch=<chapter id>&k=<l|q> — the
// Notes-with-Back view opened from Revision (BRIEFS C8.1 + C7.5, design
// _v2/design/NotesFromOneLiner.dc.html).
//
// The Notes screen in the full-screen layout with the dark pinned bar "Back to Revision"
// ("Your revision is waiting for you / N done · Your answers are safe"), no menu, no
// outline; the place is boxed ("The one-liner came from this line" for a line, "The answer
// to this question is here" for a question) and "I have read it. Back to Revision" follows
// it. Back = the session page, which shows the same answered item again (kept in
// sessionStorage by AppRevisionSession). Events qnotes_open / qnotes_read / qnotes_back.
import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { track } from '../../components/app/activity';
import { BackBar } from '../../components/app/NotesBack';
import { C } from '../../components/app/theme';
import { NotesScreen } from './AppNotes';
import { useQnotesTracking } from './AppTestNotes';
import { revLastKey } from './AppRevisionSession';

function readLast(id) {
  try {
    const raw = sessionStorage.getItem(revLastKey(id));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export default function AppRevisionNotes() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const at = params.get('at') || null;
  const chapterId = params.get('ch') || null;
  const kind = params.get('k') === 'q' ? 'question' : 'one_liner';
  const last = readLast(id);
  const item = last && last.item;
  const done = last && last.res && last.res.counts ? last.res.counts.done : null;
  const questionId = item && item.kind === 'question' ? item.question.id : undefined;
  const oneLinerId = item && item.kind === 'one_liner' ? item.one_liner.id : undefined;
  const target = last && last.res && last.res.target;

  const goBack = useCallback((button) => {
    track('qnotes_back', {
      test_id: id, question_id: questionId, one_liner_id: oneLinerId,
      details: { from: 'revision', kind, button },
    });
    if (location.state && location.state.v2back && window.history.length > 1) navigate(-1);
    else navigate(`/app/revision/${id}`, { replace: true });
  }, [id, questionId, oneLinerId, kind, location.state, navigate]);

  const qmode = useMemo(() => ({
    number: null, at,
    backLabel: 'Back to Revision',
    endLabel: 'I have read it. Back to Revision',
    line1: 'Your revision is waiting for you',
    line2: done != null ? `${done} done · Your answers are safe` : 'Your answers are safe',
    boxText: (k) => (kind === 'question'
      ? (k === 'unit' ? 'Read this part for this question' : 'The answer to this question is here')
      : (k === 'unit' ? 'Read this part for this line' : 'The one-liner came from this line')),
    onBack: goBack,
  }), [at, done, kind, goBack]);

  useQnotesTracking(chapterId ? {
    test_id: id, question_id: questionId, one_liner_id: oneLinerId, chapter_id: Number(chapterId),
    number: null, point_code: target ? target.point_code : undefined,
    section_code: target ? target.section_code : undefined, level: target ? target.level : undefined,
    at, from: 'revision',
  } : null);

  if (!chapterId) {
    return (
      <>
        <BackBar qmode={qmode} />
        <div data-testid="v2-qn-missing" style={{ padding: '28px 32px', fontSize: 16, color: C.text }}>
          This place in the notes was not found.
        </div>
      </>
    );
  }
  return <NotesScreen key={`${chapterId}|${at || ''}`} chapterId={String(chapterId)} at={at} qmode={qmode} />;
}
